import type { ErpModule } from '@/api/platform.api';

export type ApprovalModuleOption = {
  value: string;
  label: string;
};

/** All approval engine owner modules (global inbox / workflow builder). */
export const ALL_APPROVAL_MODULES: ApprovalModuleOption[] = [
  { value: 'HR', label: 'HRMS' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'CRM', label: 'CRM' },
  { value: 'PROJECTS', label: 'Projects' },
  { value: 'DESK', label: 'Help Desk' },
  { value: 'PROCUREMENT', label: 'Procurement' },
  { value: 'INVENTORY', label: 'Inventory' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'SOCIAL', label: 'Social' },
  { value: 'POS', label: 'POS' },
  { value: 'LEAD_GEN', label: 'Lead Generation' },
];

/**
 * Which subscribed/login apps unlock each approval module in the UI.
 * Most map 1:1; inventory-style approvals appear when POS, procurement, or WMS is enabled.
 */
const APPROVAL_MODULE_ACCESS: Record<string, ErpModule[]> = {
  HR: ['HR'],
  FINANCE: ['FINANCE'],
  CRM: ['CRM'],
  PROJECTS: ['PROJECTS'],
  DESK: ['DESK'],
  PROCUREMENT: ['PROCUREMENT', 'POS'],
  INVENTORY: ['POS', 'PROCUREMENT', 'WAREHOUSE'],
  WAREHOUSE: ['WAREHOUSE', 'POS'],
  MARKETING: ['MARKETING'],
  SOCIAL: ['SOCIAL'],
  POS: ['POS'],
  LEAD_GEN: ['LEAD_GEN'],
};

export const APPROVAL_ENTITY_ACTIONS: Record<string, string[]> = {
  HR: ['LEAVE', 'ATTENDANCE', 'OVERTIME', 'EXPENSE', 'PROMOTION', 'TRANSFER', 'PAYROLL', 'RECRUITMENT'],
  FINANCE: ['EXPENSE', 'JOURNAL', 'BUDGET', 'PAYMENT', 'REFUND'],
  CRM: ['DISCOUNT', 'QUOTATION', 'DEAL'],
  PROJECTS: ['TIMESHEET', 'BUDGET', 'RESOURCE_REQUEST'],
  PROCUREMENT: ['PURCHASE_REQUEST', 'RFQ', 'QUOTATION', 'PO', 'VENDOR_BILL'],
  INVENTORY: ['STOCK_ADJUSTMENT', 'STOCK_TRANSFER', 'STOCK_WRITE_OFF'],
  WAREHOUSE: ['TRANSFER', 'CYCLE_COUNT'],
  MARKETING: ['CAMPAIGN', 'BUDGET'],
  SOCIAL: ['PUBLISHING', 'CAMPAIGN'],
  POS: ['REFUND', 'DISCOUNT_OVERRIDE', 'PRICE_OVERRIDE', 'VOID'],
  DESK: ['TICKET_ESCALATION'],
  LEAD_GEN: ['CAMPAIGN'],
};

export function filterApprovalModulesForUser(accessibleErpModules: Set<ErpModule>): ApprovalModuleOption[] {
  return ALL_APPROVAL_MODULES.filter((option) => {
    const required = APPROVAL_MODULE_ACCESS[option.value];
    if (!required?.length) return false;
    return required.some((code) => accessibleErpModules.has(code));
  });
}

export function buildApprovalModuleSelectOptions(
  accessible: ApprovalModuleOption[],
  includeAll = true,
): ApprovalModuleOption[] {
  if (accessible.length <= 1) {
    return accessible;
  }
  return includeAll ? [{ value: '', label: 'All modules' }, ...accessible] : accessible;
}

export function resolveApprovalModuleFilter(
  current: string,
  accessible: ApprovalModuleOption[],
): string {
  if (accessible.length === 0) return '';
  if (accessible.length === 1) return accessible[0].value;
  if (!current) return '';
  if (accessible.some((m) => m.value === current)) return current;
  return '';
}
